// src/organoid/rightTab/BankingInfoGroup.js
import styles from './BankingInfoGroup.module.css';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Modal from 'react-modal';
import BankingForm from './BankingForm';
import StockTracing from './StockTracing';
import { useTranslation } from 'react-i18next';
import BankingInfoStatsModal from './BankingInfoStatsModal';
import BankingInfo from './BankingInfo';
import StockBadge from "../../components/common/StockBadge";
import filterIcon from "../../img/common/filter.png";

Modal.setAppElement('#root');

export default function BankingInfoGroup() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { tmpKey, timeKey } = useParams();
    const MYCO_OPTIONS = ['NA', 'Negative', 'Positive'];

    // ─── 상태 선언 ───
    const [teamName, setTeamName] = useState('');
    const [bankingInfoGroup, setBankingInfoGroup] = useState([]);
    const [patientCode, setPatientCode] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [tracingItem, setTracingItem] = useState(null);

    const [showFilterModal, setShowFilterModal] = useState(false);
    const [showStatsModal, setShowStatsModal] = useState(false);
    const [showBankingList, setShowBankingList] = useState(false);
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isTracingModalOpen, setIsTracingModalOpen] = useState(false);

    // 로그인 사용자 정보 (email)
    const [userInfo, setUserInfo] = useState({});

    // Organoid Key 그룹 편집 상태 (추가됨)
    const [editingGroupOrgKey, setEditingGroupOrgKey] = useState(null);
    const [groupOrgKeyInput, setGroupOrgKeyInput] = useState('');

    // Mycoplasma 그룹 편집 상태
    const [editingGroupMyco, setEditingGroupMyco] = useState(null);
    const [groupMycoInput, setGroupMycoInput] = useState('');

    // Culture Media 그룹 편집 상태
    const [editingGroupCulture, setEditingGroupCulture] = useState(null);
    const [groupCultureInput, setGroupCultureInput] = useState('');

    // Thawing No 그룹 편집 상태
    const [editingGroupThawing, setEditingGroupThawing] = useState(null);
    const [groupThawingInput, setGroupThawingInput] = useState('');

    // 필터 옵션들
    const [filterOptions, setFilterOptions] = useState({
        passageNo: '', cultureMedia: '', freezingDate: '', keeper: ''
    });
    const [passageNoOptions, setPassageNoOptions] = useState([]);
    const [cultureMediaOptions, setCultureMediaOptions] = useState([]);
    const [freezingDateOptions, setFreezingDateOptions] = useState([]);
    const [keeperOptions, setKeeperOptions] = useState([]);

    // 페이지네이션
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;

    // ─── 날짜 문자열을 YYYY-MM-DD로 통일 헬퍼 ───
    const normalizeDateString = (dateString) => {
        if (!dateString) return '';
        if (/^\d{8}$/.test(dateString)) {
            const y = dateString.slice(0, 4),
                m = dateString.slice(4, 6),
                d = dateString.slice(6, 8);
            return `${y}-${m}-${d}`;
        }
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            return dateString;
        }
        return dateString;
    };

    // ─── 상세 모달로 넘길 때 날짜 보정(빈값 → '0000-00-00') ───
    const patchGroupForDetails = (group) => {
        const raw = (group?.FreezingDate ?? '').toString().trim();
        const patchedFD = raw === ''
            ? '0000-00-00'
            : normalizeDateString(raw);
        return { ...group, FreezingDate: patchedFD };
    };

    // ─── 헬퍼: 날짜 포맷팅 ───
    const formatDate = dateString => {
        const d = new Date(dateString),
            Y = d.getFullYear(),
            M = String(d.getMonth() + 1).padStart(2, '0'),
            D = String(d.getDate()).padStart(2, '0'),
            h = String(d.getHours()).padStart(2, '0'),
            m = String(d.getMinutes()).padStart(2, '0'),
            s = String(d.getSeconds()).padStart(2, '0'),
            ms = String(d.getMilliseconds()).padStart(3, '0');
        return `${Y}-${M}-${D} ${h}:${m}:${s}.${ms}`;
    };

    // ─── useEffects ───

    // 0) 권한 정보 가져오기
    useEffect(() => {
        fetch('/api/getAuthLevel', { method: 'POST' })
            .then(response => {
                if (!response.ok) throw new Error(`HTTP error, status = ${response.status}`);
                return response.json();
            })
            .then(data => {
                if (data) setTeamName(data.userTeamName);
            })
            .catch(error => console.log(error));
    }, []);

    // 1) 그룹 데이터 가져오기
    const fetchBankingGroup = useCallback(() => {
        fetch('/api/getBankingGroupInfo', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ param: tmpKey })
        })
            .then(r => r.json())
            .then(data => {
                setBankingInfoGroup(data);
            });
    }, [tmpKey]);

    useEffect(() => {
        fetchBankingGroup();
    }, [fetchBankingGroup]);

    // 2) URL 상 timeKey 있으면 자동 조회 모달 열기
    useEffect(() => {
        if (!timeKey || bankingInfoGroup.length === 0) return;
        const decoded = decodeURIComponent(timeKey);
        const group = bankingInfoGroup.find(item =>
            item.times?.split(',').includes(decoded)
        );
        if (group) {
            const patched = patchGroupForDetails({ ...group, time: decoded });
            setSelectedItem(patched);
            setShowBankingList(true);
        }
    }, [timeKey, bankingInfoGroup]);

    // 3) 환자 코드 가져오기
    useEffect(() => {
        fetch('/api/getPatientCode', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ param: tmpKey })
        })
            .then(r => r.json())
            .then(data => setPatientCode(data));
    }, [tmpKey]);

    // 4) 로그인 사용자 정보 가져오기
    useEffect(() => {
        fetch('/api/getLoginedUserInfo', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        })
            .then(r => r.json())
            .then(data => setUserInfo(data.data[0] || {}));
    }, []);

    // 5) 필터 옵션 목록 생성
    useEffect(() => {
        setPassageNoOptions([...new Set(bankingInfoGroup.map(i => i.PassageNo))]);
        setCultureMediaOptions([...new Set(bankingInfoGroup.map(i => i.CultureMedia))]);
        setFreezingDateOptions([...new Set(bankingInfoGroup.map(i => i.FreezingDate))]);
        setKeeperOptions([...new Set(bankingInfoGroup.map(i => i.Keeper))]);
    }, [bankingInfoGroup]);

    // ─── 필터링 & 페이지네이션 ───
    const filtered = bankingInfoGroup.filter(item =>
        (!filterOptions.passageNo || item.PassageNo === filterOptions.passageNo) &&
        (!filterOptions.cultureMedia || item.CultureMedia === filterOptions.cultureMedia) &&
        (!filterOptions.freezingDate || item.FreezingDate === filterOptions.freezingDate) &&
        (!filterOptions.keeper || item.Keeper === filterOptions.keeper)
    );
    const pageItems = filtered.slice(
        (currentPage - 1) * itemsPerPage, currentPage * itemsPerPage
    );
    const handleFilterChange = e => {
        const { name, value } = e.target;
        setFilterOptions(prev => ({ ...prev, [name]: value }));
    };

    // ─── 모달 핸들러 ───
    const closeBankingList = () => {
        setSelectedItem(null);
        setShowBankingList(false);
        if (location.state?.fromTracing) {
            navigate(-1);
        } else {
            navigate(`/organoid/${tmpKey}/bank`);
        }
    };

    const openModal = useCallback(item => {
        setSelectedItem(item);
        setIsFormModalOpen(true);
        if (item.time) {
            navigate(`/organoid/${tmpKey}/bank/${encodeURIComponent(item.time)}`);
        } else {
            navigate(`/organoid/${tmpKey}/bank`);
        }
    }, [navigate, tmpKey]);

    const closeFormModal = () => {
        setIsFormModalOpen(false);
        setSelectedItem(null);
        navigate(`/organoid/${tmpKey}/bank`);
    };

    const openTracingModal = item => {
        setTracingItem(item);
        setIsTracingModalOpen(true);
    };
    const closeTracingModal = () => {
        setTracingItem(null);
        setIsTracingModalOpen(false);
    };

    // ─── CRUD 핸들러 ───
    const handleRetrieve = async newItem => {
        Object.assign(newItem, { Vessel: '', Line: '', Box: '', Position: '0' });
        newItem.time = formatDate(newItem.time);
        try {
            const res = await fetch('/api/updateBankingInfo', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ param: newItem })
            });
            const json = await res.json();
            if (json.message) alert(json.message);
            fetchBankingGroup();
            closeFormModal();
            return json.message;
        } catch {
            alert('서버 통신 오류');
            return 'err';
        }
    };

    const handleDelete = itemToDelete => {
        itemToDelete.time = formatDate(itemToDelete.time);
        fetch('/api/deleteBankingInfo', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ param: itemToDelete })
        })
            .then(r => r.json())
            .then(json => {
                alert(json.message || '삭제 실패');
                fetchBankingGroup();
            });
        closeFormModal();
    };

    const handleSave = (newItem, isEditMode) => {
        newItem.PatientCode = patientCode[0]?.PatientCode;
        newItem.Organoid_Info_Key = tmpKey;
        const endpoint = isEditMode
            ? '/api/updateBankingInfo'
            : '/api/insertBankingInfo';
        fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ param: newItem })
        })
            .then(r => r.json())
            .then(json => {
                if (json.message === 'duplication') {
                    alert('해당 위치에 이미 데이터가 저장되어 있습니다.');
                } else if (json.message === 'All positions are occupied') {
                    alert('모든 위치가 차지되어 있습니다.');
                } else {
                    alert(json.message || '저장 완료');
                    fetchBankingGroup();
                    closeFormModal();
                }
            });
    };

    // ─── ★ [통합됨] 필드 일괄 수정 API 함수 ───
    const saveGroupFieldChange = async (group, column, value) => {
        // #을 hashmark로 치환 (Organoid_Info_Key 변경 시 대비)
        const processedValue = value.replaceAll('#', 'hashmark');
        const times = group.times.split(',');

        for (const t of times) {
            await fetch('/api/updateBankingGroupField', { 
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    param: {
                        Organoid_Info_Key: tmpKey,
                        time: t,
                        column: column, // 'Organoid_Info_Key', 'CultureMedia', 'thawingNo', 'Mycoplasma'
                        value: processedValue
                    },
                    email: userInfo.email
                })
            });
        }

        const displayNames = {
            Organoid_Info_Key: 'Organoid Key',
            CultureMedia: 'Culture Media',
            thawingNo: 'Thawing No.',
            Mycoplasma: 'Mycoplasma'
        };

        alert(`${displayNames[column]}가 그룹 전체에 적용되었습니다.`);
        
        // 각 모드 종료
        if (column === 'Organoid_Info_Key') {
            cancelGroupEditOrgKey();
            // 키값이 변경되었으므로 페이지를 새로고침하거나 변경된 키로 이동하는 것이 안전할 수 있습니다.
            // 필요 시 아래 주석 해제하여 리다이렉션 처리
            // navigate(`/organoid/${processedValue}/bank`); 
        }
        if (column === 'CultureMedia') cancelGroupEditCulture();
        if (column === 'thawingNo') cancelGroupEditThawing();
        if (column === 'Mycoplasma') cancelGroupEditMyco();
        
        fetchBankingGroup();
    };

    // ─── 필드별 Edit 토글 함수들 ───
    // Organoid Key 편집 함수 추가
    const startGroupEditOrgKey = group => {
        setEditingGroupOrgKey(group);
        setGroupOrgKeyInput(group.Organoid_Info_Key?.replace('hashmark', '#') || '');
    };
    const cancelGroupEditOrgKey = () => {
        setEditingGroupOrgKey(null);
        setGroupOrgKeyInput('');
    };

    const startGroupEditCulture = group => {
        setEditingGroupCulture(group);
        setGroupCultureInput(group.CultureMedia || '');
    };
    const cancelGroupEditCulture = () => {
        setEditingGroupCulture(null);
        setGroupCultureInput('');
    };

    const startGroupEditThawing = group => {
        setEditingGroupThawing(group);
        setGroupThawingInput(group.thawingNo || '');
    };
    const cancelGroupEditThawing = () => {
        setEditingGroupThawing(null);
        setGroupThawingInput('');
    };

    const startGroupEditMyco = group => {
        setEditingGroupMyco(group);
        const cur = (group.Mycoplasma || '').trim();
        setGroupMycoInput(MYCO_OPTIONS.includes(cur) ? cur : 'NA');
    };
    const cancelGroupEditMyco = () => {
        setEditingGroupMyco(null);
        setGroupMycoInput('');
    };

    // ─── 필터 모달 렌더 ───
    const renderFilterModal = () => (
        <Modal
            isOpen={showFilterModal}
            onRequestClose={() => setShowFilterModal(false)}
            style={customStyles}
        >
            <h2>Filter Options</h2>
            <div>
                <label>Passage No:</label>
                <select name="passageNo" value={filterOptions.passageNo} onChange={handleFilterChange}>
                    <option value="">All</option>
                    {passageNoOptions.map((o, i) => <option key={i} value={o}>{o}</option>)}
                </select>
            </div>
            <div>
                <label>Culture Media:</label>
                <select name="cultureMedia" value={filterOptions.cultureMedia} onChange={handleFilterChange}>
                    <option value="">All</option>
                    {cultureMediaOptions.map((o, i) => <option key={i} value={o}>{o}</option>)}
                </select>
            </div>
            <div>
                <label>Freezing Date:</label>
                <select name="freezingDate" value={filterOptions.freezingDate} onChange={handleFilterChange}>
                    <option value="">All</option>
                    {freezingDateOptions.map((o, i) => <option key={i} value={o}>{normalizeDateString(o)}</option>)}
                </select>
            </div>
            <div>
                <label>Keeper:</label>
                <select name="keeper" value={filterOptions.keeper} onChange={handleFilterChange}>
                    <option value="">All</option>
                    {keeperOptions.map((o, i) => <option key={i} value={o}>{o}</option>)}
                </select>
            </div>
            <button onClick={() => setShowFilterModal(false)}>Apply Filters</button>
        </Modal>
    );

    return (
        <div className={styles.bankingInfo}>
            {/* 상단 */}
            <div className={styles.bankingHeaderGrid}>
                <div className={styles.bankingHeader}>{t('뱅킹 정보')}</div>
                <button onClick={() => setShowFilterModal(true)} className={styles.bankingHeaderButton}>
                    <img src={filterIcon} className={styles.iconImgSize} alt="filter" /> {t('필터')}
                </button>
                <button onClick={() => openModal({ exist: 'null', patientCode, mode: 'add' })} className={styles.bankingHeaderButton}>
                    {t('추가')}
                </button>
                {renderFilterModal()}
                <button onClick={() => setShowStatsModal(true)} className={styles.bankingHeaderButton}>
                    {t('통계')}
                </button>
                <Modal
                    isOpen={showStatsModal}
                    onRequestClose={() => setShowStatsModal(false)}
                    overlayClassName={styles.statsModalOverlay}
                    className={styles.statsModalContent}
                >
                    <BankingInfoStatsModal onClose={() => setShowStatsModal(false)} organoidKey={tmpKey} />
                </Modal>
            </div>

            {/* 리스트 & 편집 UI */}
            <div className={styles.bankingInfoBottom}>
                <div className={styles.bankingTabLeftSide}>
                    <div>&nbsp;</div>
                    <div className={styles.bankingLeftTitle}>Group Information</div>
                    <div className={styles.bankingLeftTitle}>Organoid Key</div>
                    <div className={styles.bankingLeftTitle}>Passage No.</div>
                    <div className={styles.bankingLeftTitle}>Culture Media</div>
                    <div className={styles.bankingLeftTitle}>Freezing Date</div>
                    <div className={styles.bankingLeftTitle}>Keeper</div>
                    <div className={styles.bankingLeftTitle}>Thawing No.</div>
                    <div className={styles.bankingLeftTitle}>Mycoplasma</div>
                </div>
                {pageItems.map((item, idx) => (
                    <div
                        key={idx}
                        className={styles.bankingTabRightSide}
                        onClick={() => {
                            const patched = patchGroupForDetails(item);
                            setSelectedItem(patched);
                            setShowBankingList(true);
                        }}
                    >
                        <div className={styles.bankingInfoData} style={{ display: 'flex', gap: 4 }}>
                            {/* 보유한 분류만 배지로 표시 (M / W / A) */}
                            {item.hasMaster > 0 && <StockBadge value="1" />}
                            {item.hasWorking > 0 && <StockBadge value="0" />}
                            {item.hasArb > 0 && <StockBadge value="2" />}
                        </div>

                        {/* [추가됨] Organoid Key 그룹 편집 칸 */}
                        <div className={styles.bankingInfoData}>
                            {editingGroupOrgKey === item ? (
                                <div className={styles.mycoEditContainer}>
                                    <input
                                        type="text"
                                        value={groupOrgKeyInput}
                                        onClick={e => e.stopPropagation()}
                                        onChange={e => { e.stopPropagation(); setGroupOrgKeyInput(e.target.value); }}
                                        className={styles.mycoSelect}
                                    />
                                    <div className={styles.mycoButtonGroup}>
                                        <button
                                            onClick={e => { e.stopPropagation(); saveGroupFieldChange(item, 'Organoid_Info_Key', groupOrgKeyInput); }}
                                            className={styles.mycoSaveButton}
                                        >
                                            저장
                                        </button>
                                        <button
                                            onClick={e => { e.stopPropagation(); cancelGroupEditOrgKey(); }}
                                            className={styles.mycoCancelButton}
                                        >
                                            취소
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.mycoDisplayContainer}>
                                    <span className={styles.textEllipsis} title={item.Organoid_Info_Key?.replace('hashmark', '#') || '-'}>
                                        {item.Organoid_Info_Key?.replace('hashmark', '#') || '-'}
                                    </span>
                                    {teamName === '중개연구팀' && (
                                        <button
                                            onClick={e => { e.stopPropagation(); startGroupEditOrgKey(item); }}
                                            className={styles.mycoEditButton}
                                        >
                                            ✏️
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className={styles.bankingInfoData}>{item.PassageNo || '-'}</div>

                        {/* Culture Media 그룹 편집 칸 */}
                        <div className={styles.bankingInfoData}>
                            {editingGroupCulture === item ? (
                                <div className={styles.mycoEditContainer}>
                                    <input
                                        type="text"
                                        value={groupCultureInput}
                                        onClick={e => e.stopPropagation()}
                                        onChange={e => { e.stopPropagation(); setGroupCultureInput(e.target.value); }}
                                        className={styles.mycoSelect}
                                    />
                                    <div className={styles.mycoButtonGroup}>
                                        <button
                                            onClick={e => { e.stopPropagation(); saveGroupFieldChange(item, 'CultureMedia', groupCultureInput); }}
                                            className={styles.mycoSaveButton}
                                        >
                                            저장
                                        </button>
                                        <button
                                            onClick={e => { e.stopPropagation(); cancelGroupEditCulture(); }}
                                            className={styles.mycoCancelButton}
                                        >
                                            취소
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.mycoDisplayContainer}>
                                    <span className={styles.textEllipsis} title={item.CultureMedia || '-'}>
                                        {item.CultureMedia || '-'}
                                    </span>
                                    {teamName === '중개연구팀' && (
                                        <button
                                            onClick={e => { e.stopPropagation(); startGroupEditCulture(item); }}
                                            className={styles.mycoEditButton}
                                        >
                                            ✏️
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className={styles.bankingInfoData}>
                            {normalizeDateString(item.FreezingDate) || '-'}
                        </div>
                        <div className={styles.bankingInfoData}>{item.Keeper || '-'}</div>

                        {/* Thawing No 그룹 편집 칸 */}
                        <div className={styles.bankingInfoData}>
                            {editingGroupThawing === item ? (
                                <div className={styles.mycoEditContainer}>
                                    <input
                                        type="text"
                                        value={groupThawingInput}
                                        onClick={e => e.stopPropagation()}
                                        onChange={e => { e.stopPropagation(); setGroupThawingInput(e.target.value); }}
                                        className={styles.mycoSelect}
                                    />
                                    <div className={styles.mycoButtonGroup}>
                                        <button
                                            onClick={e => { e.stopPropagation(); saveGroupFieldChange(item, 'thawingNo', groupThawingInput); }}
                                            className={styles.mycoSaveButton}
                                        >
                                            저장
                                        </button>
                                        <button
                                            onClick={e => { e.stopPropagation(); cancelGroupEditThawing(); }}
                                            className={styles.mycoCancelButton}
                                        >
                                            취소
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.mycoDisplayContainer}>
                                    <span className={styles.textEllipsis} title={item.thawingNo || '-'}>
                                        {item.thawingNo || '-'}
                                    </span>
                                    {teamName === '중개연구팀' && (
                                        <button
                                            onClick={e => { e.stopPropagation(); startGroupEditThawing(item); }}
                                            className={styles.mycoEditButton}
                                        >
                                            ✏️
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Mycoplasma 그룹 편집 칸 */}
                        <div className={styles.bankingInfoData}>
                            {editingGroupMyco === item ? (
                                <div className={styles.mycoEditContainer}>
                                    <select
                                        value={groupMycoInput}
                                        onClick={e => e.stopPropagation()}
                                        onChange={e => { e.stopPropagation(); setGroupMycoInput(e.target.value); }}
                                        className={styles.mycoSelect}
                                    >
                                        {MYCO_OPTIONS.map(opt => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </select>
                                    <div className={styles.mycoButtonGroup}>
                                        <button
                                            onClick={e => { e.stopPropagation(); saveGroupFieldChange(item, 'Mycoplasma', groupMycoInput); }}
                                            className={styles.mycoSaveButton}
                                        >
                                            저장
                                        </button>
                                        <button
                                            onClick={e => { e.stopPropagation(); cancelGroupEditMyco(); }}
                                            className={styles.mycoCancelButton}
                                        >
                                            취소
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.mycoDisplayContainer}>
                                    <span className={styles.textEllipsis} title={item.Mycoplasma || '-'}>
                                        {item.Mycoplasma || '-'}
                                    </span>
                                    {/* Mycoplasma는 권한 제한 없이 항상 렌더링됨 */}
                                    <button
                                        onClick={e => { e.stopPropagation(); startGroupEditMyco(item); }}
                                        className={styles.mycoEditButton}
                                    >
                                        ✏️
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                ))}

                {/* 조회용 모달 */}
                <Modal isOpen={showBankingList} onRequestClose={closeBankingList} style={customStyles}>
                    <BankingInfo item={selectedItem} />
                </Modal>
            </div>

            {/* 페이지네이션 */}
            <div className={styles.pagination}>
                {Array.from({ length: Math.ceil(filtered.length / itemsPerPage) }, (_, i) => i + 1).map(p =>
                    <button
                        key={p}
                        className={p === currentPage ? styles.activePage : ''}
                        onClick={() => setCurrentPage(p)}
                    >{p}</button>
                )}
            </div>

            {/* 부모 추적 모달 */}
            <Modal isOpen={isTracingModalOpen} onRequestClose={closeTracingModal} style={customStyles}>
                <StockTracing
                    time={tracingItem?.time}
                    item={tracingItem}
                    onCancel={closeTracingModal}
                />
            </Modal>

            {/* 추가/수정 폼 모달 */}
            <Modal isOpen={isFormModalOpen} onRequestClose={closeFormModal} style={customStyles}>
                <h2>
                    {selectedItem?.mode === 'add'
                        ? 'Add Banking Information under'
                        : selectedItem
                            ? 'Edit Banking Information'
                            : 'Add Banking Information'}
                </h2>
                <BankingForm
                    mode={selectedItem?.mode}
                    time={selectedItem?.time}
                    item={selectedItem}
                    onSave={handleSave}
                    onCancel={closeFormModal}
                    onDelete={handleDelete}
                    onRetrieve={handleRetrieve}
                    isEditMode={!!selectedItem && selectedItem.mode !== 'add'}
                    patientCode={patientCode[0]?.PatientCode || ''}
                />
            </Modal>
        </div>
    );
}

const customStyles = {
    content: { width: 'fit-content', height: 'fit-content', margin: 'auto' }
};